<template>
  <div :class="{ row: shouldCreateRow }">
    <!-- Special Case: OneOf -> Select which object schema to show -->
    <div v-if="schema.oneOf">
      <ApiSchemaOneAnyOf
        schema-type="oneOf"
        :schema-array="schema.oneOf"
        :example-generator-args="exampleGeneratorArgs"
        :type="type"
      />
    </div>
    <div v-else-if="schema.anyOf">
      <ApiSchemaOneAnyOf
        schema-type="anyOf"
        :schema-array="schema.anyOf"
        :example-generator-args="exampleGeneratorArgs"
        :type="type"
      />
    </div>
    <div v-else-if="isAtRoot && itemsVariants && itemsSchemaType">
      <api-schema-property :schema="schema" :example-generator-args="exampleGeneratorArgs" :type="type" />
    </div>
    <div v-else>
      <!-- Special Case: root is array -> Show headless array definition -->
      <api-schema-property v-if="isAtRoot && schema.items" :schema="schema" />
      <!-- Anything else -->
      <api-schema-properties v-else-if="schema.properties" :properties="schema.properties" />

      <div
        v-else-if="schema.enum"
        id="enum-available-values"
        class="inline-block max-w-[100%] wrap-break-word text-gray-500"
      >
        <div class="inline-block">{{ $t('documentation.api.schema.availableValues') }}</div>
        <span v-for="(value, index) in schema.enum" :key="value + index" class="enum inline-block">
          <code>{{ value }}</code>
          <span v-if="index < schema.enum.length - 1">,</span>
        </span>
      </div>
    </div>
    <api-content-examples
      v-if="shouldShowExamples"
      :key="itemsVariants ? arrayItemPosition : 0"
      :type="type"
      :example-generator-args="effectiveExampleGeneratorArgs"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, provide, ref } from 'vue';
import {
  ExampleGeneratorArgs,
  Schema,
  SchemaOneOf,
  SchemaAnyOf,
  SchemaArray,
} from '@bifrost-dev-docs-mfe/common/types/apiDocs';
import { OpenAPIV3 } from 'openapi-types';
import ApiSchemaProperties from '@/components/api/content/operations/schema/ApiSchemaProperties.vue';
import ApiSchemaProperty from '@/components/api/content/operations/schema/ApiSchemaProperty.vue';
import ApiContentExamples from '@/components/api/content/common/ApiContentExamples.vue';
import ApiSchemaOneAnyOf from '@/components/api/content/operations/schema/ApiSchemaOneAnyOf.vue';

const props = defineProps({
  schema: { type: Object as PropType<Schema>, required: true },
  type: { type: String as PropType<'request' | 'payload' | 'response'>, required: false },
  exampleGeneratorArgs: { type: Object as PropType<ExampleGeneratorArgs>, required: false },
});

/// Are we still at root, i.e. not in a row
const isAtRoot = computed(() => !!props.type);

const itemsVariants = computed(() => {
  if (!isAtRoot.value) return null;
  const { items } = props.schema as SchemaArray;
  if (!items) return null;
  return (items as SchemaAnyOf).anyOf || (items as SchemaOneOf).oneOf || null;
});

const itemsSchemaType = computed((): 'anyOf' | 'oneOf' | undefined => {
  const { items } = props.schema as SchemaArray;
  if (!items) return undefined;
  if ((items as SchemaAnyOf).anyOf) return 'anyOf';
  if ((items as SchemaOneOf).oneOf) return 'oneOf';
  return undefined;
});

const shouldCreateRow = computed(
  () => isAtRoot.value && !(props.schema as SchemaOneOf).oneOf && !(props.schema as SchemaAnyOf).anyOf
);

const arrayItemPosition = ref(0);
const setArrayItemPosition = (index: number) => {
  arrayItemPosition.value = index;
};

if (itemsVariants.value) {
  provide('setArrayItemPosition', setArrayItemPosition);
}

const effectiveExampleGeneratorArgs = computed(() => {
  if (!itemsVariants.value || !props.exampleGeneratorArgs) return props.exampleGeneratorArgs;
  const schemaItems = props.exampleGeneratorArgs.schema.items as OpenAPIV3.SchemaObject | undefined;
  const variants = (schemaItems?.anyOf || schemaItems?.oneOf) as OpenAPIV3.SchemaObject[] | undefined;
  if (!variants?.length) return props.exampleGeneratorArgs;
  const selectedItem = variants[arrayItemPosition.value];
  return {
    ...props.exampleGeneratorArgs,
    schema: { ...props.exampleGeneratorArgs.schema, items: selectedItem } as OpenAPIV3.SchemaObject,
    title: selectedItem.description,
  };
});

const shouldShowExamples = computed(() => shouldCreateRow.value);
</script>
